import { AlertTriangle, Clock } from "lucide-react";
import { cn } from "@/lib/utils";
import type { ObjectiveCategory } from "@/types";
import type { CategoryRollup } from "./okr-overview";
import { CATEGORY_LABEL } from "./okrConstants";
import { ProgressRing } from "./ProgressRing";

interface OkrCategoryCardsProps {
  rollups: CategoryRollup[];
  activeCategory: ObjectiveCategory | "";
  onSelectCategory: (category: ObjectiveCategory | "") => void;
}

/** One card per objective category; click toggles it as the page's category filter. */
export function OkrCategoryCards({ rollups, activeCategory, onSelectCategory }: OkrCategoryCardsProps) {
  return (
    <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
      {rollups.map(({ category, count, avgProgress, overdueCount, dueSoonCount }) => {
        const selected = activeCategory === category;
        return (
          <button
            key={category}
            type="button"
            onClick={() => onSelectCategory(selected ? "" : category)}
            className={cn(
              "flex items-center gap-3 rounded-xl border bg-card p-5 text-left transition-colors hover:bg-muted/40",
              selected ? "border-primary ring-1 ring-primary" : "border-border",
            )}
          >
            <div className="relative flex-shrink-0">
              <ProgressRing value={avgProgress} />
              <div className="absolute inset-0 flex items-center justify-center text-[10px] font-semibold">
                {avgProgress}%
              </div>
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold">{CATEGORY_LABEL[category]}</p>
              <p className="text-xs text-muted-foreground">
                {count} objective{count === 1 ? "" : "s"}
              </p>
              {(overdueCount > 0 || dueSoonCount > 0) && (
                <div className="mt-1 flex items-center gap-2">
                  {overdueCount > 0 && (
                    <span className="inline-flex items-center gap-0.5 text-[11px] font-medium text-destructive">
                      <AlertTriangle className="h-3 w-3" /> {overdueCount} overdue
                    </span>
                  )}
                  {dueSoonCount > 0 && (
                    <span className="inline-flex items-center gap-0.5 text-[11px] font-medium text-amber-600 dark:text-amber-500">
                      <Clock className="h-3 w-3" /> {dueSoonCount} due soon
                    </span>
                  )}
                </div>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}
